import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Trash2, ShieldBan, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { DataTable } from '@/components/common/DataTable';
import { formatDate } from '@/utils';
import { useDebounce } from '@/hooks';
import { useGetVendorsQuery, useUpdateVendorStatusMutation } from '@/store/api/vendorApi';

/* 🔥 replace with your actual api slice */

/* ===================== TYPES ===================== */

export type VendorStatus = 'Active' | 'Blocked';

export interface Vendor {
  id: number;
  name: string;
  category: 'Legal' | 'Compliance';
  email: string;
  phone?: string;
  website?: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  status: VendorStatus;
}

/* ===================== STATUS BADGE ===================== */

function VendorStatusBadge({ status }: { status: VendorStatus }) {
  const config: Record<VendorStatus, any> = {
    Active: {
      label: 'Active',
      container: 'bg-green-100 text-green-800',
      dot: 'bg-green-500',
    },
    Blocked: {
      label: 'Disabled',
      container: 'bg-red-100 text-red-800',
      dot: 'bg-red-500',
    },
  };

  const { label, container, dot } = config[status];

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${container}`}>
      <span className={`mr-1 h-2 w-2 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

/* ===================== ACTIONS ===================== */

function VendorActions({ vendor }: { vendor: Vendor }) {
  const [updateVendorStatus] = useUpdateVendorStatusMutation();

  const handleUpdate = async (data: { isActive?: boolean; isDeleted?: boolean }) => {
    await updateVendorStatus({
      vendorId: vendor.id,
      ...data,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {vendor.status !== 'Active' && (
          <DropdownMenuItem onClick={() => handleUpdate({ isActive: true })}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            Activate
          </DropdownMenuItem>
        )}

        {vendor.status === 'Active' && (
          <DropdownMenuItem onClick={() => handleUpdate({ isActive: false })}>
            <ShieldBan className="mr-2 h-4 w-4" />
            Disable
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="text-destructive"
          onClick={() => handleUpdate({ isDeleted: true })}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ===================== PAGE ===================== */

export function LegalComplianceVendorsPage() {
  const navigate = useNavigate();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isError } = useGetVendorsQuery({
    skip: pageIndex * pageSize,
    take: pageSize,
    search: debouncedSearch,
  });

  const vendors: Vendor[] =
    data?.data?.map((v: any) => ({
      ...v,
      status: v.isActive ? 'Active' : 'Blocked',
    })) ?? [];

  const totalCount = data?.count ?? 0;

  const columns: ColumnDef<Vendor>[] = [
    {
      header: 'Vendor Name',
      cell: ({ row }) => <span className="font-semibold">{row.original.name}</span>,
    },
    {
      header: 'Category',
      cell: ({ row }) => <span>{row.original.category}</span>,
    },
    {
      header: 'Email',
      cell: ({ row }) => (
        <a href={`mailto:${row.original.email}`} className="text-primary underline">
          {row.original.email}
        </a>
      ),
    },
    {
      header: 'Status',
      cell: ({ row }) => <VendorStatusBadge status={row.original.status} />,
    },
    {
      header: 'Created',
      cell: ({ row }) => <span>{formatDate(new Date(row.original.createdAt))}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <VendorActions vendor={row.original} />,
      enableSorting: false,
    },
  ];

  if (isLoading) return <div className="p-6">Loading vendors...</div>;
  if (isError) return <div className="p-6 text-red-500">Failed to load vendors</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Legal & Compliance Vendors</h2>

        {/* <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => (window.location.href = 'mailto:admin@yourapp.com')}
          >
            Suggest Vendor
          </Button>

          <Button onClick={() => navigate('/vendors/create')}>Upload Vendor</Button>
        </div> */}
      </div>

      <DataTable
        data={vendors}
        columns={columns}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageCount={Math.ceil(totalCount / pageSize)}
        manualPagination
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;

          setPageIndex(next.pageIndex);
          setPageSize(next.pageSize);
        }}
        onSearchChange={(value) => {
          setSearch(value);
          setPageIndex(0);
        }}
      />
    </div>
  );
}
